/**
 * Utilitário para gerenciar parâmetros UTM
 * Captura, armazena e recupera parâmetros UTM da URL
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const UTM_STORAGE_KEY = 'vox2you_utm_params';
const UTM_EXPIRY_KEY = 'vox2you_utm_expiry';
const UTM_EXPIRY_DAYS = 30; // Parâmetros UTM expiram após 30 dias

/**
 * Captura parâmetros UTM da URL atual
 */
export function captureUTMParams(): UTMParams {
  const params: UTMParams = {};
  const urlParams = new URLSearchParams(window.location.search);

  const utmKeys: (keyof UTMParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
}

/**
 * Salva parâmetros UTM no localStorage com data de expiração
 */
export function saveUTMParams(params: UTMParams): void {
  if (Object.keys(params).length === 0) {
    return;
  }

  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    
    // Definir data de expiração
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + UTM_EXPIRY_DAYS);
    localStorage.setItem(UTM_EXPIRY_KEY, expiryDate.toISOString());
  } catch (error) {
    console.error('Erro ao salvar parâmetros UTM:', error);
  }
}

/**
 * Recupera parâmetros UTM do localStorage
 * Retorna null se não houver parâmetros ou se estiverem expirados
 */
export function getStoredUTMParams(): UTMParams | null {
  try {
    const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
    const expiryDate = localStorage.getItem(UTM_EXPIRY_KEY);

    if (!storedParams) {
      return null;
    }

    // Verificar se os parâmetros expiraram
    if (expiryDate) {
      const expiry = new Date(expiryDate);
      const now = new Date();
      
      if (now > expiry) {
        // Parâmetros expirados, limpar storage
        clearUTMParams();
        return null;
      }
    }

    return JSON.parse(storedParams);
  } catch (error) {
    console.error('Erro ao recuperar parâmetros UTM:', error);
    return null;
  }
}

/**
 * Limpa parâmetros UTM do localStorage
 */
export function clearUTMParams(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(UTM_EXPIRY_KEY);
  } catch (error) {
    console.error('Erro ao limpar parâmetros UTM:', error);
  }
}

/**
 * Inicializa a captura de UTM
 * Deve ser chamado quando a aplicação carrega
 */
export function initUTMTracking(): void {
  const urlParams = captureUTMParams();
  
  // Se houver parâmetros UTM na URL, salvar (sobrescreve os anteriores)
  if (Object.keys(urlParams).length > 0) {
    saveUTMParams(urlParams);
    console.log('Parâmetros UTM capturados:', urlParams);
  }
}

/**
 * Obtém parâmetros UTM para envio no formulário
 * Retorna um objeto vazio se não houver parâmetros
 */
export function getUTMParamsForSubmit(): UTMParams {
  const storedParams = getStoredUTMParams();
  return storedParams || {};
}
